/**
 * Team → Workload — prototype VIEWS.workload (docs/prototype.html L1203): "Preview morning digest" in the head,
 * four stats (hidden inside the Team hub by the prototype CSS, as there), then one row per ops person with Open,
 * Overdue, Today, Done this week and the load meter. A row opens the person: their pending list, most important
 * first, and the message to send (prototype openMember / buildMsg), with Copy and Send on WhatsApp (a wa.me link).
 *
 * Data: GET /team/workload (load_pct and its colour are the server's, ADR §8.9). The pending list is
 * GET /tasks?status=open&assignee_id. The phone for WhatsApp comes from GET /users when the caller may see it.
 * The digest preview is composed on the page from the same numbers. ADR-001 §10 defers outbound email and push,
 * so nothing is sent on a timer: the preview says so, and each person's list goes out from their row.
 */
import { useMemo, useState } from 'react';
import { ROLE_LABEL, type Task, type WorkloadPerson } from '@amplispot/contracts';
import { firstName, todayIso } from '../../lib/format';
import { useUsers } from '../../lib/data';
import { useSession } from '../../lib/session';
import { useWorkload } from '../../lib/team';
import { dueText, useAllTasks } from '../../lib/work';
import { Avatar, Drawer, Empty, ErrorState, Loading, Modal, useToast } from '../../ui/kit';
import { Lbl } from '../../shell/Help';
import { Stat } from '../timesheets/TimesheetReport';
import { COPY_FAILED, copyText } from '../../lib/clipboard';

const PRI: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

/** Prototype sortImportance: overdue first (most overdue), then priority, then the nearest due date. */
export function byImportance(a: Task, b: Task): number {
  return Number(b.overdue) - Number(a.overdue) || b.days_overdue - a.days_overdue || (PRI[a.priority] ?? 9) - (PRI[b.priority] ?? 9) || a.due.localeCompare(b.due);
}

/** Prototype buildMsg. */
export function buildMsg(name: string, tasks: Task[]): string {
  const f = firstName(name);
  if (!tasks.length) return `Hi ${f}, you have nothing pending today. Thank you.`;
  return (
    `Hi ${f}, here is your pending list, most important first:\n\n` +
    tasks
      .map((t, i) => `${i + 1}. ${t.overdue ? '[Overdue] ' : ''}${t.priority === 'urgent' || t.priority === 'high' ? '[Priority] ' : ''}${t.title}${t.client ? ` (${t.client.name})` : ''}`)
      .join('\n') +
    '\n\nPlease update status in Ops by 11 am. Thank you.'
  );
}

/**
 * The message the drawer offers, or null while there is nothing true to say: the person's task list is still
 * loading or failed to load. Building it from an empty list then would send "nothing pending" to someone who
 * may have overdue work, so Copy and Send stay off until the list has loaded.
 */
export function drawerMessage(name: string, tasks: { isPending: boolean; isError: boolean }, list: Task[], edited: string | null): string | null {
  if (tasks.isPending || tasks.isError) return null;
  return edited ?? buildMsg(name, list);
}

export function Workload() {
  const q = useWorkload();
  const [open, setOpen] = useState<WorkloadPerson | null>(null);
  const [digest, setDigest] = useState(false);

  if (q.isPending) return <Loading label="Loading the team’s workload…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { totals, people } = q.data;

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn" onClick={() => setDigest(true)}>
            Preview morning digest
          </button>
        </div>
      </div>
      <div className="stats">
        <Stat k="Open tasks" v={totals.open} />
        <Stat k="Overdue" v={totals.overdue} cls="red" />
        <Stat k="Due today" v={totals.due_today} cls="amber" />
        <Stat k="Done this week" v={totals.done_this_week} cls="green" />
      </div>
      <div className="panel">
        {people.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th><Lbl>Person</Lbl></th>
                  <th><Lbl>Open</Lbl></th>
                  <th><Lbl>Overdue</Lbl></th>
                  <th><Lbl>Today</Lbl></th>
                  <th><Lbl>Done this week</Lbl></th>
                  <th><Lbl>Load</Lbl></th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.user.id} className="row" onClick={() => setOpen(p)}>
                    <td>
                      <div className="split">
                        <Avatar user={p.user} />
                        <div>
                          <b>{p.user.name}</b>
                          <div className="sub">{ROLE_LABEL[p.user.role]}</div>
                        </div>
                      </div>
                    </td>
                    <td className="num">{p.open}</td>
                    <td>{p.overdue ? <span className="chip cold">{p.overdue}</span> : <span className="chip hot">0</span>}</td>
                    <td className="num">{p.due_today}</td>
                    <td className="num">{p.done_this_week}</td>
                    <td style={{ minWidth: 140 }} title={`${p.load_pct}% of a full load (${q.data.tasks_per_full_load} open tasks)`}>
                      <div className={`meter ${p.load === 'red' ? 'cold' : p.load === 'amber' ? 'warm' : 'hot'}`} role="meter" aria-valuenow={p.load_pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${p.user.name} load`}>
                        <i style={{ width: `${p.load_pct}%` }} />
                      </div>
                    </td>
                    <td className="tr">
                      <button className="btn sm">Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty title="No one in ops yet">Invite the Ops associate director and members in Settings → People.</Empty>
        )}
      </div>
      {open ? <PersonDrawer p={open} onClose={() => setOpen(null)} /> : null}
      {digest ? (
        <Modal
          title="Morning digest"
          sub="Each person’s open and overdue tasks this morning. It is not sent on a timer in this version: send each person’s list on WhatsApp from their row."
          onClose={() => setDigest(false)}
          footer={
            <button className="btn" onClick={() => setDigest(false)}>
              Close
            </button>
          }
        >
          <div style={{ display: 'grid', gap: 8 }}>
            {people.map((p) => (
              <div className="split" key={p.user.id}>
                <Avatar user={p.user} />
                <b>{firstName(p.user.name)}</b>
                <div className="grow" />
                <span className="chip">{p.open} open</span>
                {p.overdue ? <span className="chip cold">{p.overdue} overdue</span> : null}
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function PersonDrawer({ p, onClose }: { p: WorkloadPerson; onClose: () => void }) {
  const { can } = useSession();
  const toast = useToast();
  const tasks = useAllTasks({ status: 'open', assignee_id: p.user.id });
  const users = useUsers(can('users.read'));
  const today = todayIso();
  const list = useMemo(() => [...(tasks.data ?? [])].filter((t) => t.assignee?.id === p.user.id).sort(byImportance), [tasks.data, p.user.id]);
  const [edited, setEdited] = useState<string | null>(null);
  const msg = drawerMessage(p.user.name, tasks, list, edited);
  const phone = (p.user.phone ?? users.data?.find((u) => u.id === p.user.id)?.phone)?.replace(/^\+/, '');

  return (
    <Drawer title={p.user.name} onClose={onClose}>
      <div className="lbl" style={{ marginBottom: 8 }}>
        Pending, most important first
      </div>
      <div className="panel" style={{ marginBottom: 16 }}>
        {tasks.isPending ? (
          <Loading label="Loading their tasks…" />
        ) : tasks.isError ? (
          <ErrorState error={tasks.error} onRetry={() => tasks.refetch()} />
        ) : list.length ? (
          list.map((t) => {
            const d = dueText(t.due, today);
            return (
              <div className="li" key={t.id}>
                <div className="b">
                  <div className="t" style={{ fontWeight: 500 }}>
                    {t.title}
                  </div>
                  <div className="m">{t.client ? t.client.name : 'Internal'}</div>
                </div>
                <span className={`chip ${d.tone}`}>{d.label}</span>
              </div>
            );
          })
        ) : (
          <div className="empty">Nothing pending</div>
        )}
      </div>
      <div className="lbl" style={{ marginBottom: 8 }}>
        Message to send
      </div>
      <textarea
        className="in"
        rows={9}
        value={msg ?? ''}
        placeholder={tasks.isError ? 'Their tasks could not be loaded, so there is no message yet. Try again above.' : 'Writing the message once their tasks load…'}
        onChange={(e) => setEdited(e.target.value)}
        aria-label="Message to send"
        disabled={msg === null}
      />
      <div className="split" style={{ marginTop: 12 }}>
        <button className="btn" disabled={msg === null} onClick={() => msg !== null && copyText(msg, () => toast('Copied'), () => toast(COPY_FAILED))}>
          Copy
        </button>
        <div className="grow" />
        {msg === null ? (
          <button className="btn good" disabled>
            Send on WhatsApp
          </button>
        ) : (
          <a className="btn good" href={`https://wa.me/${phone ?? ''}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer">
            Send on WhatsApp
          </a>
        )}
      </div>
    </Drawer>
  );
}
